import { useMemo } from 'react';
import { APP_BASE_PATH } from 'shared/appBasePath';
import { buildNavigationUrl } from 'shared/routes';
import { LifecycleState } from 'api/GraphQL/queries/getVehicleSummaries';

type UseVehicleUrlsParams = {
  acquisitionName: string;
  appraisalId: string;
  isOfferAccepted: boolean;
  lifecycleState: LifecycleState;
  isTradeIn?: boolean;
};

type VehicleUrls = {
  url: string;
};

/**
 * Custom hook to construct the appropriate URL for a vehicle based on its lifecycle state.
 * Returns the correct URL for either viewing an offer/hub or resubmitting an expired offer.
 */
export const useVehicleUrls = ({
  acquisitionName,
  appraisalId,
  isOfferAccepted,
  lifecycleState,
  isTradeIn
}: UseVehicleUrlsParams): VehicleUrls =>
  useMemo(() => {
    // For expired offers, determine the resubmit URL
    if (lifecycleState === LifecycleState.Expired) {
      return {
        url: buildNavigationUrl({
          baseUrl: `${APP_BASE_PATH}/refresh/appraisal/${appraisalId}`,
          includeReferrer: false
        })
      };
    }

    // Trade-ins that are scheduled or already applied to a purchase continue to the top-level
    // Purchase flow (mirrors the Hub "Continue With Purchase" link). Non-trade sales still go to
    // the Hub. No APP_BASE_PATH prefix — /purchase is a top-level Carvana route.
    if (
      (lifecycleState === LifecycleState.Scheduled ||
        lifecycleState === LifecycleState.AppliedToPurchase) &&
      isTradeIn === true
    ) {
      return {
        url: buildNavigationUrl({
          baseUrl: '/purchase',
          includeReferrer: false
        })
      };
    }

    // For all other states, determine the offer or hub URL
    const baseUrlOfferOrHub =
      isOfferAccepted && lifecycleState.toLowerCase() !== 'rejected'
        ? `${APP_BASE_PATH}/hub/${acquisitionName}`
        : `${APP_BASE_PATH}/offer/${appraisalId}`;

    return {
      url: buildNavigationUrl({
        baseUrl: baseUrlOfferOrHub,
        includeReferrer: false
      })
    };
  }, [acquisitionName, appraisalId, isOfferAccepted, lifecycleState, isTradeIn]);
